import useAppStore from '@/h5/stores/app'
import LGSdk from '@/packages/plugins/languageSDK/index'
import AppReq from '@/h5/utils/appActions/index'
import UnityBridge from '@/h5/utils/appActions/unityBridge'
import { randomNum, getTimestamp, getSignedData } from '@/packages/plugins/utils'
import { customRequest, baseSecret } from '@/h5/api/activity'
import { ref, onMounted, onBeforeUnmount, reactive, watch } from 'vue'
import Toast from '@/h5/components/toast'
import ConfirmDialog from '@/h5/components/confirmDialog'
import EmptyImg from '@/packages/components/business/luckyGrid/src/assets/img/empty.png'
import useComponentRef from '@/packages/plugins/hook/useComponent'
import EventBus from '@/h5/utils/eventBus'

/** localStorage：是否跳过抽奖动画，true/false（按活动 ID 区分） */
const getRaffleSkipAnimationStorageKey = (activityId) => `xy_raffle_skip_animation_${activityId || 'global'}`

const RAFFLE_EVENTS_COMMON = [
  {
    name: 'chooseBigReward',
    label: '选中大奖',
    params: [
      {
        label: '消耗项下标', // 表单名称
        value: 'index' // 表单model字段名称
      }
    ]
  },
  {
    name: 'getMoneyBalances',
    label: '刷新货币余额'
  }
]

export const RAFFLE_EVENTS_BTN = [
  {
    name: 'startRaffleBTN',
    label: '一键翻牌'
  },
  {
    name: 'startRaffleTimes',
    label: '指定翻牌次数',
    params: [
      {
        label: '翻牌次数',
        value: 'drawCount'
      },
      {
        label: '动画节点',
        value: 'animationNodeUUID',
        type: 'selectNode'
      },
      {
        label: '等待动画时间(毫秒) ',
        value: 'waitTime'
      }
    ]
  },
  {
    name: 'startRaffleWithID',
    label: '翻牌-自定义布局',
    params: [
      {
        label: '卡牌索引',
        value: 'index'
      },
      {
        label: '双面图片ID',
        value: 'cardId'
      }
    ]
  },
  {
    name: 'resetRaffle',
    label: '重置奖池-自定义布局'
  },
  {
    name: 'toggleRaffleAnimation',
    label: '开启/关闭抽奖动画'
  },
  ...RAFFLE_EVENTS_COMMON
]

export const RAFFLE_EVENTS = [
  {
    name: 'startRaffle',
    label: '一键翻牌'
  },
  {
    name: 'reset',
    label: '重置奖池'
  },
  ...RAFFLE_EVENTS_COMMON
]

export const RAFFLE_DATA_SOURCE = [
  {
    label: '代币余额',
    keys: 'moneyBalance'
  },
  {
    label: '代币余额-数量',
    keys: 'moneyBalance[].num'
  },
  {
    label: '代币余额-图标',
    keys: 'moneyBalance[].icon'
  },
  {
    label: '用户累计抽奖轮次',
    keys: 'userRaffle.round'
  },
  {
    label: '用户当前大奖项已抽次数',
    keys: 'userRaffle.roundDrawCount'
  },
  {
    label: '当前大奖保底次数',
    keys: 'userRaffle.bigMaxCount'
  },
  {
    label: '总抽奖次数',
    keys: 'userRaffle.totalDrawCount'
  },
  {
    label: '已抽中大奖ID',
    keys: 'hasBigRewardId'
  },
  {
    label: '当前选中大奖ID',
    keys: 'grandPrizeID'
  },
  {
    label: '当前选中大奖-图片',
    keys: 'userRaffle.bigRewardSrc'
  },
  {
    label: '当前选中大奖-消耗值',
    keys: 'userRaffle.bigRewardNum'
  },
  {
    label: '当前选中大奖-消耗图片',
    keys: 'userRaffle.bigRewardNumSrc'
  },
  {
    label: '当前选中大奖-奖品列表',
    keys: 'userRaffle.bigRewardVoList'
  },
  {
    label: '奖品列表(如选中大奖,第一个为大奖奖品)',
    keys: 'grandPrizeList'
  },
  {
    label: '奖品列表-奖项id',
    keys: 'grandPrizeList[].id'
  },
  {
    label: '奖品列表-图片',
    keys: 'grandPrizeList[].src'
  },
  {
    label: '奖品列表-数量',
    keys: 'grandPrizeList[].count'
  },
  {
    label: '奖品列表-是否已抽中',
    keys: 'grandPrizeList[].isDrawn'
  },
  {
    label: '奖品列表-是否为大奖',
    keys: 'grandPrizeList[].isBigReward'
  },
  {
    label: '当前选中大奖-奖品列表-数量',
    keys: 'userRaffle.bigRewardVoList[].count'
  },
  {
    label: '大奖列表',
    keys: 'userRaffle.bigRewardList'
  },
  {
    label: '大奖列表-未选中',
    keys: 'userRaffle.bigRewardList[].unSelected'
  },
  {
    label: '大奖列表-奖品图片',
    keys: 'userRaffle.bigRewardList[].src'
  },
  {
    label: '大奖列表-奖项背景',
    keys: 'userRaffle.bigRewardList[].backgroundSrc'
  },
  {
    label: '大奖列表-总抽次数',
    keys: 'userRaffle.bigRewardList[].optionalTotalNum'
  },
  {
    label: '大奖列表-可抽次数',
    keys: 'userRaffle.bigRewardList[].optionalNum'
  },
  {
    label: '大奖列表-解锁轮次',
    keys: 'userRaffle.bigRewardList[].unlockRound'
  },
  {
    label: '大奖列表-奖品列表',
    keys: 'userRaffle.bigRewardList[].itemVoList',
    description: '奖品列表-{count: 数量, type: 类型, contentId: 内容ID}'
  },
  {
    label: '轮次奖励列表',
    keys: 'userRaffle.roundRewardList'
  },
  {
    label: '轮次奖励列表-可领取轮次',
    keys: 'userRaffle.roundRewardList[].acceptRound'
  },
  {
    label: '轮次奖励列表-是否已领取',
    keys: 'userRaffle.roundRewardList[].isAccepted'
  },
  {
    label: '累计抽取奖励列表',
    keys: 'userRaffle.drawCountRewardList'
  },
  {
    label: '累计抽取奖励列表-累计次数',
    keys: 'userRaffle.drawCountRewardList[].acceptRound'
  },
  {
    label: '累计抽取奖励列表-是否已领取',
    keys: 'userRaffle.drawCountRewardList[].isAccepted'
  },
  {
    label: '抽奖结果列表',
    keys: 'userRaffle.rewardOutList'
  },
  {
    label: '抽奖结果列表-奖项数量',
    keys: 'userRaffle.rewardOutList[].count'
  },
  {
    label: '抽奖结果列表-奖项图片',
    keys: 'userRaffle.rewardOutList[].src'
  },
  {
    label: '抽奖结果列表-是否为大奖',
    keys: 'userRaffle.rewardOutList[].isBigReward'
  },
  {
    label: '抽奖记录列表',
    keys: 'userRaffle.raffleRecord'
  },
  {
    label: '抽奖记录列表-是否为大奖',
    keys: 'userRaffle.raffleRecord[].isBigReward'
  },
  {
    label: '抽奖记录列表-时间',
    keys: 'userRaffle.raffleRecord[].time'
  },
  {
    label: '抽奖记录列表-奖励列表',
    keys: 'userRaffle.raffleRecord[].rewardVoList'
  },
  {
    label: '抽奖记录列表-奖励列表-图片',
    keys: 'userRaffle.raffleRecord[].rewardVoList[].src'
  },
  {
    label: '抽奖记录列表-奖励列表-数量',
    keys: 'userRaffle.raffleRecord[].rewardVoList[].count'
  },
  {
    label: '是否跳过抽奖动画',
    keys: 'skipRaffleAnimation'
  }
]

export const API = {
  /**
   * 获取活动配置
   * @param {*} activityId
   * @returns
   */
  getActivityConfig: (isNewVersion = false, activityId) => {
    const params = {
      id: activityId,
      sign: '',
      appId: 1004,
      timestamp: getTimestamp(),
      random: randomNum(6),
      activityType: isNewVersion ? 50 : 4
    }
    params.sign = getSignedData(params, baseSecret)
    return customRequest(null, '/v1/activityManage/configList', 'get', { 'Content-Type': 'application/json; charset=utf-8' }, params)
  },
  /**
   * 抽奖
   * @param {*} activityId
   * @param {*} wayId
   * @returns
   */
  OptionalLotteryDrawReq: (isNewVersion = false, activityId, oneKeyDraw, startIndex, drawCount) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi(isNewVersion ? '30' : '18', { activityId, oneKeyDraw, selectIndex: startIndex + 1, drawCount })
    } else {
      return null
    }
  },
  /**
   * 选择大奖
   * @param {*} activityId
   * @param {*} bigRewardId
   * @returns
   */
  OptionalLotterySelectBigRewardReq: (isNewVersion = false, activityId, bigRewardId, resetRewardData = true) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi(isNewVersion ? '29' : '17', { activityId, bigRewardId, resetRewardData })
    } else {
      return null
    }
  },
  /**
   * 获取信息
   * @param {*} activityId
   * @returns
   */
  OptionalLotteryInfoReq: (isNewVersion = false, activityId) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi(isNewVersion ? '28' : '16', { activityId })
    } else {
      return null
    }
  },
  /**
   * 查询抽奖记录
   * @param {*} activityId
   * @returns
   */
  OptionalLotteryRecordReq: (isNewVersion = false, activityId) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi(isNewVersion ? '31' : '19', {
        activityId,
        current: 1, //第几页，从1开始
        size: 50 //每页大小，每页最大50个
      })
    } else {
      return null
    }
  },
  /**
   * 重置抽奖池
   * @param {*} activityId
   * @returns
   */
  OptionalLotteryResetPoolReq: (isNewVersion = false, activityId) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi(isNewVersion ? '32' : '20', { activityId })
    } else {
      return null
    }
  },
  /**
   * 获取用户代币余额
   * @param {*} ids
   * @returns
   */
  getUserCurrencys: (ids) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi('1', ids)
    } else {
      return null
    }
  },
  /**
   * 获取用户常量
   * @returns
   */
  getUserConstant: () => {
    return new Promise((resolve) => {
      UnityBridge.callHandler('getUserConstant', (_, data) => {
        let constant = {
          diamond: 0,
          sex: 0,
          coin: 0,
          surpriseCoin: 0
        }
        if (_ === false) {
          return resolve(constant)
        }
        try {
          const params = JSON.parse(data)
          constant = Object.assign(constant, params)
          return resolve(constant)
        } catch (e) {
          console.error(e)
          return resolve(constant)
        }
      })
    })
  }
}

export const useRaffle = (props, initRaffleFunc, setPrizesFunc, setPrizesResultFunc, findPrizeImgFunc, findPrizeItemVoImgFunc, isBtn = false) => {
  // 抽奖配置
  const componentRef = ref(null)
  const raffleRef = ref(null)
  // style 属性变化监听（v-show 等指令对根节点样式的改写无法通过响应式追踪）
  let styleObserver = null
  const appStore = useAppStore ? useAppStore() : null

  const allComponentRef = useComponentRef()

  // 组件属性
  const state = reactive({
    // 语言
    language: 'en',
    // 组件uuid
    uuid: '',
    // 活动配置
    raffleCfg: {},
    // 抽奖结果
    raffleResult: {},
    // 保存已抽奖的奖项index
    saveAwardIndex: [],
    // 是否抽奖中
    isRaffle: false,
    // 挂载数据
    bigReward: null,
    dataSource: {
      hasBigRewardId: '',
      grandPrizeID: '',
      // 当前大奖奖品列表（奖池奖品 + 抽中状态，选中大奖的配置在首位）
      grandPrizeList: [],
      moneyBalance: [],
      userRaffle: {},
      // false 不跳过抽奖动画，true 跳过（与 localStorage 同步）
      skipRaffleAnimation: false
    },
    // 自定义卡牌ID
    customCardID: []
  })

  // 初始化存储卡牌ID
  const saveCardID = (id) => {
    if (!state.customCardID.includes(id)) {
      state.customCardID.push(id)
    }
  }

  const initSkipRaffleAnimationFromStorage = () => {
    try {
      const raw = localStorage.getItem(getRaffleSkipAnimationStorageKey(props.activityId))
      state.dataSource.skipRaffleAnimation = raw === 'true'
    } catch {
      state.dataSource.skipRaffleAnimation = false
    }
    appStore?.setPageDataSource(state.uuid, state.dataSource || null)
  }

  /** 开启/关闭抽奖动画：切换 true/false 并写入 localStorage */
  const toggleRaffleAnimation = () => {
    const next = !state.dataSource.skipRaffleAnimation
    state.dataSource.skipRaffleAnimation = next
    try {
      localStorage.setItem(getRaffleSkipAnimationStorageKey(props.activityId), String(next))
    } catch {
      /* ignore */
    }
    appStore?.setPageDataSource(state.uuid, state.dataSource || null)
  }

  // 点击翻牌按钮(指定次数)
  const startRaffleTimes = async (data) => {
    await startRaffle({
      drawCount: Number(data.drawCount),
      waitTime: Number(data.waitTime),
      animationNodeUUID: data.animationNodeUUID
    })
      .catch((e) => {
        throw e
      })
      .finally(() => {
        // 隐藏动画节点（跳过动画时未展示，无需处理）
        if (!state.dataSource.skipRaffleAnimation && data.animationNodeUUID?.length > 0) {
          const uuid = data.animationNodeUUID[data.animationNodeUUID.length - 1]
          appStore.setNodeAttribute(uuid, 'hiden', true)
        }
      })
  }

  // 点击翻牌按钮(一键)—自定义布局
  const startRaffleBTN = async () => {
    await startRaffle().catch((e) => {
      throw e
    })
  }

  // 点击翻牌按钮(单个)—自定义布局
  const startRaffleWithID = async (data) => {
    await startRaffle({
      index: Number(data.index),
      cardId: data.cardId
    }).catch((e) => {
      throw e
    })
  }

  // 翻牌动画—自定义布局
  const startBTN = (index, cardId) => {
    allComponentRef.getComponentRef(cardId)?.start(index)
  }

  // 重置奖池—自定义布局
  const resetRaffle = () => {
    reset()
  }

  // 重置动画
  const resetAllBTN = () => {
    for (let i = 0; i < state.customCardID.length; i++) {
      allComponentRef.getComponentRef(state.customCardID[i])?.reset()
    }
  }

  // 点击翻牌按钮
  const startRaffle = async (raffleData = {}) => {
    // 获取当前时间搓
    const currentTime = new Date().getTime()
    const { index, cardId, drawCount, waitTime, animationNodeUUID } = raffleData
    if (state.isRaffle) {
      // Toast && Toast({ message: LGSdk.t('clipRaffle.clickTooFast'), duration: 1500 })
      return
    }
    const isBatch = typeof index !== 'number' && !drawCount

    // 先判断代币是否充足等
    const isContinue = await showTips(isBatch).catch((e) => {
      throw e
    })

    if (!isContinue) {
      return
    }
    state.isRaffle = true
    // 抽奖按钮自选翻牌，展示动画节点（跳过动画时不展示绑定节点）
    if (!state.dataSource.skipRaffleAnimation && animationNodeUUID?.length > 0) {
      const uuid = animationNodeUUID[animationNodeUUID.length - 1]
      appStore.setNodeAttribute(uuid, 'hiden', false)
    }
    try {
      // 抽奖请求
      let startIndex
      if (isBatch || drawCount) {
        let index = 0
        while (state.saveAwardIndex.includes(index)) {
          index++
        }
        startIndex = index
      } else {
        startIndex = index
      }
      const res = await API.OptionalLotteryDrawReq(props.isNewVersion, props.activityId, isBatch, startIndex, drawCount)
      if (res.data.status === 1) {
        // 抽奖成功
        const currentIndex = []
        state.raffleResult = res.data || {}
        // 桥接接口的 rewardList 可能为 {} 等非数组，统一归一化
        state.raffleResult.rewardList = Array.isArray(res.data.rewardList) ? res.data.rewardList : []
        // 是否批量判断
        if (isBatch || drawCount) {
          state.raffleResult.rewardList.forEach(() => {
            let addIndex = 0
            while (state.saveAwardIndex.includes(addIndex)) {
              addIndex++
            }
            state.saveAwardIndex.push(addIndex)
            currentIndex.push(addIndex)
          })
        } else {
          state.saveAwardIndex.push(index)
          currentIndex.push(index)
        }
        // 将抽奖结果赋值到每个牌里面
        console.log('---xxx---', state.raffleResult)
        setPrizesResultFunc(state.raffleResult, null, state.language)
        // 更新数据（余额/记录等刷新失败时不能阻断翻牌动画，动画在下方 startRaffleQueue 触发）
        try {
          await getRaffleGetInfo(false)
        } catch (e) {
          console.error('抽奖后刷新信息失败', e)
        }
        // 根据结果处理翻牌动画
        if (drawCount) {
          let wonBigReward = false
          // 是否已抽中大奖判断
          const hasBigReward = state.raffleResult?.rewardList?.some((item) => item.rewardId === state.dataSource.grandPrizeID)
          if (hasBigReward) {
            state.dataSource.hasBigRewardId = state.dataSource.grandPrizeID
            state.dataSource.grandPrizeID = ''
            wonBigReward = true
          } else {
            state.dataSource.hasBigRewardId = ''
          }
          // 判断当前是否有抽奖结果，有则赋值到抽奖结果列表
          const rewardOutList = []
          state.raffleResult.rewardList.forEach((reward) => {
            if (reward.itemVoList?.length) {
              reward.itemVoList.forEach((itemVo) => {
                const key = `${itemVo.type},${itemVo.contentId}`
                if (props.prizeImages[key]) {
                  const src = props.prizeImages[key].languages.find((item) => item.language === state.language)?.src || ''
                  rewardOutList.push({
                    count: itemVo.count,
                    src: src,
                    isBigReward: reward.isBigReward
                  })
                }
              })
            }
          })
          setSelectBigRewardInfo()
          state.dataSource.userRaffle.rewardOutList = rewardOutList
          appStore.setPageDataSource(state.uuid, state.dataSource || null)
          // 指定次数翻牌动画，等待翻牌结束
          // 获取当前时间搓，等待翻牌结束, 有配置需要等待时间的话
          const newCurrentTime = new Date().getTime()
          const intervalTime = newCurrentTime - currentTime
          if (!state.dataSource.skipRaffleAnimation && waitTime && intervalTime < waitTime) {
            // 抽奖按钮自选翻牌，展示动画节点（跳过动画时不展示绑定节点）
            if (animationNodeUUID?.length > 0) {
              const uuid = animationNodeUUID[animationNodeUUID.length - 1]
              appStore.setNodeAttribute(uuid, 'hiden', false)
            }
            await new Promise((resolve) => setTimeout(resolve, waitTime - intervalTime))
          }
          state.isRaffle = false
          if (wonBigReward) {
            await autoSelectBigReward()
          }
        } else {
          // 自定义布局翻牌动画
          startRaffleQueue(currentIndex, cardId, drawCount)
        }
      } else {
        const statusCodes = [3800001, 3800005, 3800006, 3800007, 3800008]
        let msg = 'error: ' + res.data.status
        if (statusCodes.includes(Number(res.data.status))) {
          msg = LGSdk.t(`clipRaffle.${res.data.status}`)
        }
        Toast && Toast({ message: msg, duration: 1500 })
        state.isRaffle = false
        if (drawCount) {
          throw new Error(`clipRaffle.${res.data.status}`)
        }
      }
    } catch (error) {
      state.isRaffle = false
      throw error
    }
  }

  // 启动抽奖动画队列
  const startRaffleQueue = async (indexArr, cardId, drawCount) => {
    const index = indexArr && indexArr.shift()
    if (index || index === 0) {
      cardId ? startBTN(index, cardId) : start(index)
    }
    if (indexArr?.length) {
      // 继续动画
      await new Promise((resolve) => setTimeout(resolve, 500))
      await startRaffleQueue(indexArr)
    } else {
      state.isRaffle = false
      // 是否已抽中大奖判断
      const hasBigReward = state.raffleResult?.rewardList?.some((item) => item.rewardId === state.dataSource.grandPrizeID)
      await new Promise((resolve) => setTimeout(resolve, 500))
      if (hasBigReward) {
        state.dataSource.hasBigRewardId = state.dataSource.grandPrizeID
        state.dataSource.grandPrizeID = ''
        if (drawCount) {
          return
        }
      } else {
        state.dataSource.hasBigRewardId = ''
      }
      // 判断是否需要确认自动选中大奖
      try {
        if (props.isAutoSelectBigReward) {
          if (hasBigReward) {
            await showBigRewardDialog('clipRaffle.autoSelectBigRewardConfirmInfo')
            await getRaffleGetInfo(false)
            await autoSelectBigReward()
          } else {
            setSelectBigRewardInfo()
            appStore.setPageDataSource(state.uuid, state.dataSource || null)
          }
        } else {
          if (hasBigReward) {
            await showBigRewardDialog('clipRaffle.getBigRewardConfirmInfo')
          }
          setSelectBigRewardInfo()
          appStore.setPageDataSource(state.uuid, state.dataSource || null)
        }
      } catch (e) {
        // 自动切换大奖链路失败不应产生未捕获异常，也不影响下一轮抽奖
        console.error('抽中大奖后处理失败', e)
      }
    }
  }

  // 抽中大奖后的提示：配置了自定义弹窗节点则展示该节点（不弹确认框），否则弹 ConfirmDialog
  const showBigRewardDialog = async (messageKey) => {
    if (props.customDialogUUID) {
      appStore.setNodeAttribute(props.customDialogUUID, 'hiden', false)
      return
    }
    ConfirmDialog &&
      (await ConfirmDialog({
        title: LGSdk.t('public.tips'),
        message: LGSdk.t(messageKey),
        confirmText: LGSdk.t('public.confirm'),
        cancelText: LGSdk.t('public.cancel')
      }))
  }

  // 开始翻牌
  const start = (index) => {
    raffleRef.value && raffleRef.value.play(index)
  }

  // 重置洗牌
  const reset = async (isSetPrizes = true) => {
    if (state.isRaffle) {
      return false
    }
    // 重置过程中不允许抽奖
    state.isRaffle = true
    // 请求重置接口，清空翻牌索引项
    // const res = await API.OptionalLotteryResetPoolReq(props.isNewVersion, props.activityId)
    // if (res.data.status === 1) {
    state.saveAwardIndex = []
    state.dataSource.userRaffle.rewardList = []
    buildGrandPrizeList()
    appStore.setPageDataSource(state.uuid, state.dataSource || null)
    isSetPrizes && setPrizes()
    isBtn ? resetAllBTN() : resetAll()
    // } else {
    //   const statusCodes = [3800001]
    //   let msg = 'error: ' + res.data.status
    //   if (statusCodes.includes(Number(res.data.status))) {
    //     msg = LGSdk.t(`clipRaffle.${res.data.status}`)
    //   }
    //   Toast && Toast({ message: msg, duration: 1500 })
    //   state.isRaffle = false
    //   return false
    // }

    state.isRaffle = false
    return true
  }

  // 一键翻牌-仅用于编辑器启动展示
  const startAll = () => {
    if (state.isRaffle) {
      return
    }
    state.isRaffle = true
    raffleRef.value &&
      raffleRef.value.playAll().then(() => {
        state.isRaffle = false
      })
  }

  // 重置动画
  const resetAll = () => {
    raffleRef.value && raffleRef.value.reset()
  }

  // 显示提示
  const showTips = async (isBatch) => {
    // 是否已选大奖
    if (!state.dataSource.grandPrizeID || !state.bigReward) {
      Toast && Toast({ message: LGSdk.t(`clipRaffle.${3800005}`), duration: 1500 })
      throw new Error(`clipRaffle.${3800005}`)
    }
    // 查询用户余额，是否足够
    const bigRewardConfig = state.raffleCfg.bigReward.find((item) => item.id === state.dataSource.grandPrizeID)

    const moneyBalance = state.dataSource.moneyBalance.find((item) => item.key === `${bigRewardConfig.expendType},${bigRewardConfig.expendItemId}`)?.num

    const cost = bigRewardConfig.expendNum
    if (moneyBalance < cost) {
      Toast && Toast({ message: LGSdk.t(`clipRaffle.${3800006}`), duration: 1500 })
      throw new Error(`clipRaffle.${3800006}`)
    }
    // 确认弹窗，是否确认自动翻牌(仅在一键翻牌情况下触发)
    if (isBatch) {
      const res =
        ConfirmDialog &&
        (await ConfirmDialog({
          title: LGSdk.t('public.tips'),
          message: LGSdk.t('clipRaffle.buyConfirmInfo'),
          confirmText: LGSdk.t('public.confirm'),
          cancelText: LGSdk.t('public.cancel')
        }))
      if (res.type === 'cancel') {
        return false
      }
    }
    return true
  }

  const findAvailableBigRewardIndex = () => {
    const bigRewardList = Array.isArray(state.dataSource.userRaffle?.bigRewardList) ? state.dataSource.userRaffle.bigRewardList : []
    const hasBigRewardId = state.dataSource.hasBigRewardId
    // 抽中大奖后，若当前大奖项仍可抽，优先继续选择同一大奖项
    if (hasBigRewardId) {
      const hasBigRewardIndex = bigRewardList.findIndex((item) => item.id === hasBigRewardId && Number(item.optionalNum ?? 0) > 0)
      if (hasBigRewardIndex >= 0) {
        return hasBigRewardIndex
      }
    }
    return bigRewardList.findIndex((item) => Number(item.optionalNum ?? 0) > 0)
  }

  // 大奖是否全部抽完（所有大奖项剩余可抽次数均<=0）
  const isAllBigRewardDrawn = () => {
    const bigRewardList = Array.isArray(state.dataSource.userRaffle?.bigRewardList) ? state.dataSource.userRaffle.bigRewardList : []
    return bigRewardList.length > 0 && bigRewardList.every((item) => Number(item.optionalNum ?? 0) <= 0)
  }

  const selectBigRewardByIndex = async (index, skipConfirm = false) => {
    const grandPrizeID = state.dataSource.userRaffle.bigRewardList[index]?.id
    if (grandPrizeID) {
      if (!skipConfirm && !props.isHideChangeBigRewardConfirmPopup) {
        const isConfirm =
          ConfirmDialog &&
          (await ConfirmDialog({
            title: LGSdk.t('public.tips'),
            message: LGSdk.t('clipRaffle.selectBigRewardConfirmInfo'),
            confirmText: LGSdk.t('public.confirm'),
            cancelText: LGSdk.t('public.cancel')
          }))
        if (isConfirm.type === 'cancel') {
          throw new Error(`clipRaffle.1000001`)
        }
      }
      const res = await API.OptionalLotterySelectBigRewardReq(props.isNewVersion, props.activityId, grandPrizeID, props.resetRewardData)
      if (res.data.status === 1) {
        if (!props.resetRewardData) {
          // 不重置大奖时, 切换大奖直接初始化配置, 回显翻牌记录
          await getRaffleGetInfo()
          return
        }
        await reset(false)
        state.dataSource.grandPrizeID = res.data.selectBigId
        let existReward = ''
        state.dataSource.userRaffle?.bigRewardList?.forEach((reward) => {
          if (reward.id === grandPrizeID) {
            existReward = reward
            reward.unSelected = false
          } else {
            reward.unSelected = true
          }
        })
        state.bigReward = existReward
        state.dataSource.userRaffle.bigRewardSrc = findPrizeItemVoImgFunc(existReward, state.language)[0]?.src || EmptyImg
        state.dataSource.hasBigRewardId = ''
        state.dataSource.userRaffle.bigMaxCount = res.data.bigMaxCount || 0
        state.dataSource.userRaffle.roundDrawCount = res.data.roundDrawCount || 0
        setSelectBigRewardInfo()
        setPrizes()
        buildGrandPrizeList()
        appStore.setPageDataSource(state.uuid, state.dataSource || null)
      } else {
        const msg = LGSdk.t(`clipRaffle.${res.data.status}`)
        Toast && Toast({ message: msg, duration: 1500 })
      }
    }
  }

  const autoSelectBigReward = async () => {
    if (!props.isAutoSelectBigReward || state.dataSource.grandPrizeID) {
      return
    }
    // 大奖全部抽完时不再执行自动切换
    if (isAllBigRewardDrawn()) {
      return
    }
    const index = findAvailableBigRewardIndex()
    if (index < 0) {
      return
    }
    await selectBigRewardByIndex(index, true)
  }

  // 选中大奖目标：未配置 index 时自动选中下一个可抽的大奖项
  const chooseBigReward = async (params) => {
    if (state.isRaffle) {
      return
    }
    const hasIndex = params?.index !== undefined && params?.index !== null && params?.index !== ''
    if (!hasIndex) {
      // 未设置 index，自动选中下一个大奖项（当前已选中大奖时不重复切换）
      if (state.dataSource.grandPrizeID) {
        return
      }
      const autoIndex = findAvailableBigRewardIndex()
      if (autoIndex < 0) {
        return
      }
      await selectBigRewardByIndex(autoIndex, false)
      return
    }
    await selectBigRewardByIndex(Number(params.index), false)
  }

  // 获取用户抽奖信息-大奖列表、轮次奖励列表等
  const getRaffleGetInfo = async (init = true) => {
    const res = await API.OptionalLotteryInfoReq(props.isNewVersion, props.activityId)
    if (res.data.status === 1) {
      // App 桥接对空列表可能返回 {} 而非 []，入口处统一归一化为数组，避免下游 forEach/map 崩溃
      state.dataSource.userRaffle = {
        ...res.data,
        rewardList: Array.isArray(res.data.rewardList) ? res.data.rewardList : [],
        bigRewardSrc: state.dataSource.userRaffle.bigRewardSrc || EmptyImg,
        bigRewardVoList: state.dataSource.userRaffle.bigRewardVoList,
        bigRewardNum: state.dataSource.userRaffle.bigRewardNum || '',
        bigRewardNumSrc: state.dataSource.userRaffle.bigRewardNumSrc || EmptyImg,
        rewardOutList: state.dataSource.userRaffle.rewardOutList || [],
        raffleRecord: state.dataSource.userRaffle.raffleRecord || []
      }
      // 大奖项排序与映射
      const prizeMap = new Map((props.prizes || []).map((prize) => [prize.id, prize]))
      const rewardMap = new Map((state.raffleCfg.bigReward || []).map((reward) => [reward.id, reward]))
      const bigRewardList = [...(Array.isArray(res.data.bigRewardList) ? res.data.bigRewardList : [])].sort((a, b) => a.sortIndex - b.sortIndex)
      // 翻牌按钮组件才有这个配置字段，没有这个字段的翻牌组件默认排序
      if (!props.isBigRewardUnSorted) {
        // 大奖项可抽次数不排序
        bigRewardList.sort((a, b) => Number(a.optionalNum === 0) - Number(b.optionalNum === 0))
      }
      state.dataSource.userRaffle.bigRewardList = bigRewardList.map((item) => {
        const prizeItem = prizeMap.get(item.id)
        const existReward = rewardMap.get(item.id)
        return {
          unSelected: !res.data.selectBigId || res.data.selectBigId !== item.id,
          backgroundSrc: findPrizeImgFunc(prizeItem, state.language) || '',
          src: findPrizeItemVoImgFunc(item, state.language)[0]?.src || '',
          optionalTotalNum: existReward?.limit || 0,
          ...item
        }
      })
      console.log('state.dataSource.userRaffle-----', state.dataSource.userRaffle)
      if (init) {
        state.dataSource.grandPrizeID = res.data.selectBigId || ''
        const existReward = res.data?.bigRewardList?.find((reward) => reward.id === state.dataSource.grandPrizeID)
        state.bigReward = existReward || null
        state.dataSource.userRaffle.bigRewardSrc = findPrizeItemVoImgFunc(state.bigReward, state.language)[0]?.src || EmptyImg
        // 初始化翻牌索引
        const newIndexArr = []
        if (res.data.rewardList?.length) {
          res.data.rewardList?.forEach((reward) => {
            newIndexArr.push(reward.index - 1)
            if (reward.rewardId === res.data.selectBigId) {
              state.dataSource.hasBigRewardId = res.data.selectBigId
            }
          })
        }
        state.saveAwardIndex = newIndexArr
        setSelectBigRewardInfo()
        setPrizes()

        // 将结果赋值给卡牌
        setPrizesResultFunc(state.dataSource.userRaffle, state.saveAwardIndex, state.language)
      }

      // 更新用户余额
      await getMoneyBalances()
      // 获取抽奖记录
      await getRaffleRecord()
      // 更新当前大奖奖品列表
      buildGrandPrizeList()
      appStore.setPageDataSource(state.uuid, state.dataSource || null)
    } else {
      const statusCodes = [3800001]
      let msg = 'error: ' + res.data.status
      if (statusCodes.includes(Number(res.data.status))) {
        msg = LGSdk.t(`clipRaffle.${res.data.status}`)
      }
      Toast && Toast({ message: msg, duration: 1500 })
    }
  }

  const getRaffleRecord = async () => {
    const recordRes = await API.OptionalLotteryRecordReq(props.isNewVersion, props.activityId)
    if (recordRes.data.recordList?.length) {
      state.dataSource.userRaffle.raffleRecord = recordRes.data.recordList.map((item) => {
        if (item.rewardVoList?.length) {
          item.rewardVoList.forEach((rewardVo) => {
            const key = `${rewardVo.type},${rewardVo.contentId}`
            if (props.prizeImages[key]) {
              rewardVo.src = props.prizeImages[key].languages.find((item) => item.language === state.language)?.src || ''
            }
          })
        }
        return item
      })
    } else {
      state.dataSource.userRaffle.raffleRecord = []
    }
  }

  const setSelectBigRewardInfo = () => {
    // 获取
    if (!state.dataSource.grandPrizeID) {
      state.dataSource.userRaffle.bigRewardNum = 0
      state.dataSource.userRaffle.bigRewardVoList = []
      state.dataSource.userRaffle.bigRewardNumSrc = EmptyImg
      return
    }
    const existReward = state.raffleCfg.bigReward.find((reward) => reward.id === state.dataSource.grandPrizeID)
    if (existReward) {
      // 设置当前大奖消耗信息
      const consumeReward = {
        itemVoList: [
          {
            type: existReward.expendType,
            contentId: existReward.expendItemId,
            count: existReward.expendNum
          }
        ]
      }
      state.dataSource.userRaffle.bigRewardNum = existReward.expendNum
      state.dataSource.userRaffle.bigRewardVoList = state.bigReward.itemVoList
      state.dataSource.userRaffle.bigRewardNumSrc = findPrizeItemVoImgFunc(consumeReward, state.language)[0]?.src || EmptyImg
    } else {
      state.dataSource.userRaffle.bigRewardNum = 0
      state.dataSource.userRaffle.bigRewardVoList = []
      state.dataSource.userRaffle.bigRewardNumSrc = EmptyImg
    }
  }

  // 组装"当前大奖奖品列表"数据源：奖池奖品配置 + 当前轮次抽中状态，选中大奖的配置置顶
  const buildGrandPrizeList = () => {
    // 添加判断是否有选中大奖
    console.log('当前大奖奖品列表------>', state.dataSource.grandPrizeID, state.dataSource.userRaffle)
    if (!state.dataSource.grandPrizeID) {
      state.dataSource.grandPrizeList = []
      return []
    }
    // 奖池奖品配置（chooseReward.rewardList，子项的 rewards 数组第一个为奖品信息）
    const poolList = Array.isArray(state.raffleCfg?.rewardList) ? state.raffleCfg.rewardList : []
    // 当前轮次已抽中的奖项id（参考 setPrizesResultFunc：userRaffle.rewardList 每项含 rewardId）
    const drawnIds = new Set()
    const drawnRewardList = Array.isArray(state.dataSource.userRaffle?.rewardList) ? state.dataSource.userRaffle.rewardList : []
    drawnRewardList.forEach((reward) => {
      drawnIds.add(reward.rewardId)
    })
    // 根据奖品 type 和 contentId 获取图片配置
    const findPrizeSrc = (type, contentId) => {
      const prizeImage = props.prizeImages[`${type},${contentId}`]
      return prizeImage?.languages?.find((item) => item.language === state.language)?.src || ''
    }
    const list = poolList.map((poolItem) => {
      const prizeInfo = poolItem.rewards?.[0] || {}
      return {
        id: poolItem.id,
        type: prizeInfo.type,
        contentId: prizeInfo.contentId,
        count: prizeInfo.count,
        src: findPrizeSrc(prizeInfo.type, prizeInfo.contentId),
        isBigReward: false,
        // 当前轮次是否已抽中（userRaffle.rewardList 子项 rewardId 与奖项id相同）
        isDrawn: drawnIds.has(poolItem.id)
      }
    })
    // 有选中大奖时，将大奖项奖品配置添加到数组第一个优先展示
    if (state.dataSource.grandPrizeID) {
      const existReward = (state.raffleCfg.bigReward || []).find((reward) => reward.id === state.dataSource.grandPrizeID)
      const bigRewardVo = existReward?.itemVoList?.[0] || state.bigReward?.itemVoList?.[0] || null
      if (bigRewardVo) {
        list.unshift({
          id: state.dataSource.grandPrizeID,
          type: bigRewardVo.type,
          contentId: bigRewardVo.contentId,
          count: bigRewardVo.count,
          src: findPrizeSrc(bigRewardVo.type, bigRewardVo.contentId),
          // 是否选中大奖项（置顶项）
          isBigReward: true,
          isDrawn: drawnIds.has(state.dataSource.grandPrizeID)
        })
      }
    }
    state.dataSource.grandPrizeList = list
    return list
  }

  // 获取用户余额信息
  const getMoneyBalances = async () => {
    let money = []
    let moneyMap = {}
    state.raffleCfg.bigReward.forEach((item) => {
      const key = item.expendType + ',' + item.expendItemId
      moneyMap[key] = 0
    })
    if (moneyMap['0,1'] !== undefined || moneyMap['0,2'] !== undefined || moneyMap['0,3'] !== undefined) {
      const res = await API.getUserConstant()
      if (moneyMap['0,1'] !== undefined) {
        const key = '0,1'
        money.push({
          key: key,
          num: Number(res.coin),
          icon: props.prizeImages[key]?.languages.find((item) => item.language === state.language)?.src || ''
        })
      }
      if (moneyMap['0,2'] !== undefined) {
        const key = '0,2'
        money.push({
          key,
          num: Number(res.diamond),
          icon: props.prizeImages[key]?.languages.find((item) => item.language === state.language)?.src || ''
        })
      }
      if (moneyMap['0,3'] !== undefined) {
        const key = '0,3'
        money.push({
          key,
          num: Number(res.surpriseCoin),
          icon: props.prizeImages[key]?.languages.find((item) => item.language === state.language)?.src || ''
        })
      }
      appStore.setPageDataSource('data-source_userConstant', res || '')
    }
    const ids = Object.keys(moneyMap)
      ?.filter((item) => !['0,1', '0,2', '0,3'].includes(item))
      ?.map((item) => item.split(',')[1])
    if (ids?.length) {
      const res = await API.getUserCurrencys({ ids })
      const currencyList = Array.isArray(res?.data?.list) ? res.data.list : []
      currencyList.forEach((item) => {
        const key = '8,' + String(item.id)
        money.push({
          key,
          num: item.count,
          icon: props.prizeImages[key]?.languages.find((item) => item.language === state.language)?.src || ''
        })
      })
    }
    state.dataSource.moneyBalance = money
    appStore.setPageDataSource(state.uuid, state.dataSource || null)
  }

  // 页面初始化
  const init = async () => {
    const res = await API.getActivityConfig(props.isNewVersion, props.activityId)
    state.raffleCfg = res?.data?.list[0]?.chooseReward || {}
    initRaffle()
    // 获取信息，暴露抽奖相关数据源
    await getRaffleGetInfo()
    await autoSelectBigReward()
    // 根据所有奖池奖项，预先加载图片资源，以免翻牌时为空白图片
    initImg()
    // 翻牌按钮，则发布
    if (isBtn) {
      EventBus.$emit('Raffle_Btn_Init')
    }
  }

  // 编辑器初始化
  const getConfig = async () => {
    if (props.activityId) {
      const res = await API.getActivityConfig(props.isNewVersion, props.activityId)
      state.raffleCfg = res?.data?.list[0]?.chooseReward || {}
      initRaffle()
      return res.data || null
    }
  }

  // 抽奖初始化
  const initRaffle = () => {
    initRaffleFunc && initRaffleFunc()
    setPrizes()
  }

  // 奖励图片初始化
  const initImg = () => {
    const imageUrls = new Set() // 使用 Set 去重
    // 奖项背景图
    props.prizes.forEach((prize) => {
      prize.languages.forEach((lan) => {
        imageUrls.add(lan.srcMale)
        imageUrls.add(lan.srcFeMale)
      })
    })
    // 奖品图片
    Object.values(props.prizeImages).forEach((item) => {
      item.languages.forEach((lan) => {
        imageUrls.add(lan.src)
      })
    })
    const validUrls = Array.from(imageUrls).filter((url) => url && (url.startsWith('http://') || url.startsWith('https://')))
    // 使用 Promise.all 并行加载图片
    Promise.all(
      validUrls.map((url) => {
        return new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = resolve
          img.onerror = (event) => {
            const error = new Error(`图片加载失败: ${url}`)
            error.originalEvent = event
            reject(error)
          }
          img.src = url
        })
      })
    ).catch((error) => {
      console.error('部分图片加载失败:', error)
    })
  }

  // 奖项初始配置赋值到卡牌上
  const setPrizes = () => {
    if (props.prizes.length === 0) {
      return
    }
    setPrizesFunc && setPrizesFunc(state.raffleCfg, state.language, state.dataSource.grandPrizeID)
  }

  // 更新抽奖
  watch(
    () => props,
    () => initRaffle(),
    {
      immediate: true,
      deep: true
    }
  )

  onMounted(() => {
    let style = ''
    if (componentRef.value) {
      state.language = componentRef.value.getAttribute('language') || 'en'
      state.uuid = componentRef.value.getAttribute('id') || ''
      style = componentRef.value.getAttribute('style')
      styleObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const style = componentRef.value.getAttribute('style')
            // 监听组件v-show的样式变化, 如果为空则代表展示, 需要重新初始化一下组件, 外部动态组件通过v-show控制适用
            if (!style) {
              if (import.meta.env.VITE_APP_TAG === 'H5') {
                init()
              } else {
                getConfig()
              }
            }
            console.log('-----翻牌组件样式', state.uuid, componentRef.value.getAttribute('style'))
          }
        })
      })
      styleObserver.observe(componentRef.value, {
        attributes: true,
        attributeFilter: ['style']
      })
    }
    initSkipRaffleAnimationFromStorage()
    // 组件通过v-show控制展示隐藏时,不调用接口初始化
    if (style && style.includes('display: none')) return
    if (import.meta.env.VITE_APP_TAG === 'H5') {
      init()
    } else {
      getConfig()
    }
  })

  onBeforeUnmount(() => {
    styleObserver && styleObserver.disconnect()
    styleObserver = null
  })

  return {
    componentRef,
    raffleRef,
    getConfig,
    reset,
    startRaffle,
    chooseBigReward,
    startAll,
    resetAll,
    getMoneyBalances,
    saveCardID,
    startRaffleBTN,
    startRaffleTimes,
    startRaffleWithID,
    resetRaffle,
    toggleRaffleAnimation
  }
}
